import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { ProgramItem, ProgramItemsApi } from '@/services/program-dashboard.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select, Checkbox, Textarea } from '@/components/ui/Input';
import { Pagination } from '@/components/ui/Pagination';

const PROGRAM_ITEM_TYPES = ['single', 'group', 'team'] as const;

const itemSchema = z.object({
  name: z.string().min(1, 'Required'),
  code: z.string().optional(),
  type: z.enum(PROGRAM_ITEM_TYPES),
  gradeCategory: z.string().optional(),
  maxMarks: z.preprocess((v) => (v === '' || v === undefined ? undefined : v), z.coerce.number().int().min(0).optional()),
  quranProgram: z.boolean(),
  indications: z.string().optional(),
  remarks: z.string().optional(),
});
type FormValues = z.infer<typeof itemSchema>;

/**
 * Program Management, Part 1 — the competition-event catalog.
 * Global, no org scoping. Write access is opened to both superadmin and
 * org-admin tokens (`OrgScopedGuard` on every method server-side, per
 * direct instruction — org admins manage this catalog now, not just
 * superadmin). `readOnly` is kept as a prop for future callers that should
 * stay view-only, but no current route passes it.
 */
export function ProgramItemsPage({ readOnly = false }: { readOnly?: boolean } = {}) {
  const qc = useQueryClient();
  const [modal, setModal] = useState<{ open: boolean; item?: ProgramItem }>({ open: false });
  const [confirm, setConfirm] = useState<{ open: boolean; item?: ProgramItem }>({ open: false });
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [quranFilter, setQuranFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['program-items-catalog-sa'],
    queryFn: () => ProgramItemsApi.list(),
  });

  const gradeOptions = useMemo(
    () => Array.from(new Set(items.map((i) => i.gradeCategory).filter((g): g is string => !!g))).sort(),
    [items],
  );

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (typeFilter && i.type !== typeFilter) return false;
      if (gradeFilter && i.gradeCategory !== gradeFilter) return false;
      if (quranFilter && (i.quranProgram ? 'yes' : 'no') !== quranFilter) return false;
      if (q && !i.name.toLowerCase().includes(q) && !(i.code ?? '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, search, typeFilter, gradeFilter, quranFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, gradeFilter, quranFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / limit));
  const pagedItems = useMemo(
    () => filteredItems.slice((page - 1) * limit, page * limit),
    [filteredItems, page],
  );

  const upsert = useMutation({
    mutationFn: (vals: { id?: string; payload: Partial<ProgramItem> }) =>
      vals.id ? ProgramItemsApi.update(vals.id, vals.payload) : ProgramItemsApi.create(vals.payload as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['program-items-catalog-sa'] });
      setModal({ open: false });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => ProgramItemsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['program-items-catalog-sa'] });
      setConfirm({ open: false });
    },
  });

  return (
    <>
      <PageHeader
        title="Program Items"
        description="The competition-event catalog — items judged in the Program Dashboard (Mark Entry, Scoreboard, etc.)."
        actions={
          readOnly ? undefined : (
            <button className="btn-primary" onClick={() => setModal({ open: true })}>
              <Plus className="mr-1.5 h-4 w-4" /> New item
            </button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or code" className="pl-9" />
        </div>
        <Select className="!w-40" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {PROGRAM_ITEM_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
        <Select className="!w-44" value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)}>
          <option value="">All Classes</option>
          {gradeOptions.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </Select>
        <Select className="!w-44" value={quranFilter} onChange={(e) => setQuranFilter(e.target.value)}>
          <option value="">Quran Program: All</option>
          <option value="yes">Quran Program: Yes</option>
          <option value="no">Quran Program: No</option>
        </Select>
      </div>

      <DataTable<ProgramItem>
        rows={pagedItems}
        getRowId={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No program items match these filters."
        columns={[
          {
            key: 'name',
            header: 'Name',
            render: (i) => <span className="font-medium text-slate-900">{i.name}</span>,
          },
          {
            key: 'code',
            header: 'Code',
            render: (i) => <code className="text-xs text-slate-600">{i.code ?? '—'}</code>,
          },
          {
            key: 'type',
            header: 'Type',
            render: (i) => <Badge tone={i.type === 'single' ? 'blue' : i.type === 'group' ? 'amber' : 'green'}>{i.type}</Badge>,
          },
          {
            key: 'gradeCategory',
            header: 'Grade',
            render: (i) => i.gradeCategory ?? '—',
          },
          {
            key: 'maxMarks',
            header: 'Max Marks',
            render: (i) => i.maxMarks ?? '—',
          },
          {
            key: 'quranProgram',
            header: 'Quran Program',
            render: (i) => (i.quranProgram ? <Badge tone="purple">Yes</Badge> : <Badge tone="slate">No</Badge>),
          },
        ]}
        actions={
          readOnly
            ? undefined
            : (i) => (
                <>
                  <button
                    className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
                    onClick={() => setModal({ open: true, item: i })}
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                    onClick={() => setConfirm({ open: true, item: i })}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              )
        }
      />

      <Pagination page={page} totalPages={totalPages} total={filteredItems.length} limit={limit} onPageChange={setPage} />

      {!readOnly && (
        <>
          <ProgramItemFormModal
            open={modal.open}
            item={modal.item}
            onClose={() => setModal({ open: false })}
            onSubmit={(vals) =>
              upsert.mutate({
                id: modal.item?.id,
                payload: {
                  name: vals.name,
                  code: vals.code || undefined,
                  type: vals.type,
                  gradeCategory: vals.gradeCategory || undefined,
                  maxMarks: vals.maxMarks,
                  quranProgram: vals.quranProgram,
                  indications: vals.indications || undefined,
                  remarks: vals.remarks || undefined,
                },
              })
            }
            saving={upsert.isPending}
            errorMsg={errMsg(upsert.error)}
          />

          <ConfirmDialog
            open={confirm.open}
            onClose={() => setConfirm({ open: false })}
            onConfirm={() => confirm.item && remove.mutate(confirm.item.id)}
            loading={remove.isPending}
            title="Delete program item?"
            message={`This will permanently delete "${confirm.item?.name}" from the catalog. Any candidate registrations or marks already recorded for it are not removed.`}
            confirmText="Delete item"
          />
        </>
      )}
    </>
  );
}

function ProgramItemFormModal({
  open,
  item,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  open: boolean;
  item?: ProgramItem;
  onClose: () => void;
  onSubmit: (v: FormValues) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(itemSchema),
    values: {
      name: item?.name ?? '',
      code: item?.code ?? '',
      type: item?.type ?? 'single',
      gradeCategory: item?.gradeCategory ?? '',
      maxMarks: item?.maxMarks ?? undefined,
      quranProgram: item?.quranProgram ?? false,
      indications: item?.indications ?? '',
      remarks: item?.remarks ?? '',
    },
  });

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={item ? `Edit ${item.name}` : 'New program item'}
      description="Global to the whole competition — not scoped to a school or organization."
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit(onSubmit)} disabled={saving}>
            {saving ? 'Saving…' : item ? 'Save changes' : 'Create item'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name" required error={errors.name?.message}>
          <Input {...register('name')} placeholder="Digital Poster Making" />
        </Field>
        <Field label="Code" error={errors.code?.message}>
          <Input {...register('code')} placeholder="DPM1" />
        </Field>

        <Field label="Type" required error={errors.type?.message}>
          <Select {...register('type')}>
            {PROGRAM_ITEM_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Grade" hint="e.g. Grade 3" error={errors.gradeCategory?.message}>
          <Input {...register('gradeCategory')} placeholder="Grade 3" />
        </Field>

        <Field label="Max marks" error={errors.maxMarks?.message}>
          <Input type="number" min={0} {...register('maxMarks')} />
        </Field>

        <div className="flex items-center pt-6">
          <Checkbox label="Quran program" {...register('quranProgram')} />
        </div>

        <Field label="Indications" hint="Instructions shown to participants/judges" className="sm:col-span-2" error={errors.indications?.message}>
          <Textarea rows={3} {...register('indications')} placeholder="Each team performs for a maximum of 5 minutes." />
        </Field>
        <Field label="Remarks" className="sm:col-span-2" error={errors.remarks?.message}>
          <Textarea rows={2} {...register('remarks')} placeholder="Props must be approved in advance." />
        </Field>

        {errorMsg && <div className="sm:col-span-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
      </form>
    </Modal>
  );
}

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
